import { useMemo, useRef, useState } from 'react';
import FilterBox from './mainComponents/FilterBox';
import FilterFixed from './mainComponents/FilterFixed';
import type { FilterKeys, PageFilterKeys, FilterOptionsMap } from './types';
import { pageFilterOptionsMap } from './constants/filterOptions';
import { addSpecialFilter, specialBooleanKeyType, uniqueArray } from './utils';
import { gameInfoType } from '@op/types/common';
import { forEach } from 'lodash-es';

function useFilterDataHook({
  gameInfo,
  renderExcludeFilter,
  pageName,
  defaultUserSynchronousValue,
  ...specialFilterBoolean
}: {
  gameInfo?: gameInfoType;
  renderExcludeFilter?: Array<FilterKeys>;
  pageName?: PageFilterKeys | Array<PageFilterKeys>;
  defaultUserSynchronousValue?: { [key: string]: any };
} & { [key in specialBooleanKeyType]?: boolean }) {
  const scrollBody = useRef<HTMLDivElement>(null);
  const otherPageParams = useRef({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<any>(null);
  const [userSynchronousValue, setUserSynchronousValue] = useState(
    defaultUserSynchronousValue,
  );
  const isFirst = useRef(true);
  const topicList = useRef<{ topic_trans: Array<any>; key_topic: any }>({
    topic_trans: [],
    key_topic: {},
  });
  const filterOptions: Array<FilterKeys> = useMemo(() => {
    const { edition_unified_id, entity_type } = gameInfo || {
      entity_type: 'mobile',
    };
    if (!pageName) return [];

    // 这里返回新数值，不然会修改到原始数据
    const getOptions = (options: Array<FilterKeys> | FilterOptionsMap) => {
      return Array.isArray(options)
        ? [...options]
        : [...(options?.[entity_type as 'mobile'] || [])];
    };

    let filter: Array<FilterKeys> = [];
    if (typeof pageName === 'string') {
      const filterOptions = pageFilterOptionsMap[pageName];
      filter = getOptions(filterOptions);
    } else {
      const filterArray = pageName.map(name =>
        getOptions(pageFilterOptionsMap[name]),
      );
      filter = uniqueArray(filterArray.flat(2));
    }

    // 添加其他的特殊过滤器
    if (edition_unified_id) {
      addSpecialFilter(filter, edition_unified_id, specialFilterBoolean);
    }

    return filter;
  }, [gameInfo]);

  // 包装renderExcludeFilter对象，保证过滤器不重复渲染
  const renderExcludeFilterMemo: Array<FilterKeys> = useMemo(() => {
    return renderExcludeFilter || [];
  }, []);

  const changeFilter = (value: any) => {
    setFilter({ ...otherPageParams.current, ...value });

    if (userSynchronousValue) {
      // 不渲染的更新数据
      forEach(userSynchronousValue, (_val, key) => {
        userSynchronousValue[key] = value[key];
      });
      setUserSynchronousValue(userSynchronousValue);
    }

    loading && setLoading(false);
    if (isFirst.current) isFirst.current = false;
  };

  const changeOtherPageParams = (value: { [key: string]: any }) => {
    otherPageParams.current = value;
  };

  const initState = () => {
    setLoading(true);
  };

  const changeTopicList = (
    value: { topic_trans: Array<any>; key_topic: any } = {
      topic_trans: [],
      key_topic: {},
    },
  ) => {
    if (Array.isArray(value.topic_trans)) {
      topicList.current = value;
    }
  };

  /**
   * 获取提交按钮的点击事件
   */
  const confirmRef = useRef<any>(null);
  function filterConfirm(noCache = false) {
    confirmRef.current?.click(noCache);
  }

  function filterReset(data?: any) {
    confirmRef.current?.reset(data);
  }

  function filterSave() {
    confirmRef.current?.save();
  }

  /**
   * 更新用户过滤配置数据
   * @param value
   */
  const changeUserSynchronousValue = (value: any, needConfirm = true) => {
    setUserSynchronousValue({ ...userSynchronousValue, ...value });
    if (needConfirm) {
      setTimeout(() => {
        filterConfirm();
      }, 100);
    }
  };

  return {
    scrollBody,
    loading,
    filter,
    changeFilter,
    filterOptions,
    initState,
    changeOtherPageParams,
    renderExcludeFilterMemo,
    isFirst: isFirst.current,
    changeTopicList,
    topicList: topicList.current,
    userSynchronousValue,
    changeUserSynchronousValue,
    filterConfirm,
    filterReset,
    filterSave,
    confirmRef,
    filterCommonParams: {
      pageName: Array.isArray(pageName) ? pageName[0] : pageName,
    },
  };
}

export { FilterBox, FilterFixed, useFilterDataHook };
